import React, { useState, useEffect } from 'react';
import {
  Box,
  Alert,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import axios from 'axios';

const GuideEmails = () => {
  const [guideEmails, setGuideEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedGuide, setSelectedGuide] = useState(null);
  const [guideName, setGuideName] = useState('');
  const [guideEmail, setGuideEmail] = useState('');

  useEffect(() => {
    loadGuideEmails();
  }, []);

  const loadGuideEmails = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get('/manifest/guide-emails');
      setGuideEmails(response.data.guide_emails || []);
    } catch (err) {
      setError('Failed to load guide emails');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (guide) => {
    setSelectedGuide(guide);
    setGuideName(guide.guide_name);
    setGuideEmail(guide.email);
    setEditDialogOpen(true);
  };

  const handleAddClick = () => {
    setSelectedGuide(null);
    setGuideName('');
    setGuideEmail('');
    setEditDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setEditDialogOpen(false);
    setSelectedGuide(null);
    setGuideName('');
    setGuideEmail('');
  };

  const handleSave = async () => {
    if (!guideName.trim() || !guideEmail.trim()) {
      setError('Guide name and email are required');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(guideEmail.trim())) {
      setError('Please enter a valid email address');
      return;
    }

    try {
      // Note: The backend endpoint is read-only for now
      // In the future, you may want to add POST/PUT/DELETE endpoints
      setError('Guide email editing is not yet implemented. The guide emails are currently managed in the backend code.');
      setTimeout(() => setError(''), 5000);
      handleCloseDialog();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save guide email');
      setTimeout(() => setError(''), 5000);
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Guide Email Mappings</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadGuideEmails}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddClick}
            disabled={loading}
          >
            Add Guide
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Guide Name</TableCell>
                <TableCell>Email Address</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {guideEmails.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    <Typography color="text.secondary">
                      No guide emails found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                guideEmails.map((guide, index) => (
                  <TableRow key={index}>
                    <TableCell>{guide.guide_name}</TableCell>
                    <TableCell>{guide.email}</TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleEditClick(guide)}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={editDialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              {selectedGuide ? 'Edit Guide Email' : 'Add Guide Email'}
            </Typography>
            <IconButton onClick={handleCloseDialog}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Guide Name"
              value={guideName}
              onChange={(e) => setGuideName(e.target.value)}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={guideEmail}
              onChange={(e) => setGuideEmail(e.target.value)}
              margin="normal"
              required
            />
            <Alert severity="info" sx={{ mt: 2 }}>
              Note: Guide email editing is currently managed in the backend code. 
              Changes made here will not be saved until backend endpoints are implemented.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GuideEmails;
